import { allocateEvenly, isLive, isMonthProtected, parseAmountToCents, tiersFromJson, tiersToJson, validTiers, type CommissionTier } from "@talentkontor/domain";
import { decimalSeparator, money } from "../../../lib/format";
import { MODEL_LABEL, MONTH_BASED, NO_MONEY, addMonthKey, itemKindCommissionable, lastDay, monthName, type MonatInfo } from "./model";
import type { CatalogEntry, DealFull, DealModel, DealStatus, ItemKind, ItemRow, PeriodFull, SettingsLite, TermsRow } from "./typen";
import { t } from "../../../lib/sprache";

/**
 * Formular „Kooperation anlegen / bearbeiten“ (deal-neu.html, AD-051 … AD-064): Zustand, Umwandlung in Datenbankzeilen,
 * Prüfung und Monatsabgleich mit Datenverlust-Schutz (KA-06, KA-07, KA-09). Rein und getestet.
 */

export const NEW_BRAND = "__neu";
export const NEW_CAMPAIGN = "__neu";
export const MAX_MONTHS = 36;

export interface MonatZeile {
  month: string;
  betrag: string;
  forecast: string;
  deadline: string;
  /** Warenwert netto des Monats (Sachleistung, nicht geteilt, keine Rechnung) */
  warenwert?: string;
}

export interface PositionZeile {
  key: string;
  id: string | null;
  kind: ItemKind;
  description: string;
  betrag: string;
  /** '' = ganze Kooperation (erster Monat), sonst YYYY-MM */
  month: string;
  commissionable: boolean;
}

export interface BillingFelder {
  legal_name: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  vat_id: string;
  register: string;
  email: string;
  reference: string;
  payment_terms_days: string;
}

export interface KoopFormular {
  title: string;
  talentId: string;
  brandId: string;
  newBrand: string;
  model: DealModel;
  status: DealStatus;
  currency: string;
  beginn: string;
  ende: string;
  honorar: string;
  satz: string;
  feeMode: "none" | "percent" | "fixed";
  feeValue: string;
  monate: MonatZeile[];
  positionen: PositionZeile[];
  platforms: string[];
  deliverables: string;
  usageRights: string;
  usageRightsUntil: string;
  link: string;
  contentDeadline: string;
  flatTaxed: boolean;
  visible: boolean;
  billing: BillingFelder;
  /** Abrechnungsweg dieser Kooperation; leer = Standard der Marke bzw. Agentur (0039) */
  route: "" | "via_agency" | "direct_to_brand";
  talentNote: string;
  internalNote: string;
  /** Exklusivität (Befund 11) */
  exklusiv: boolean;
  exklusivBis: string;
  exklusivFuer: string;
  /** Kampagne: '' = keine, NEW_CAMPAIGN = neue mit Titel newCampaign */
  campaignId: string;
  newCampaign: string;
  /** Vereinbarte Korrekturschleifen; leer = Vertrag bzw. Einstellung */
  korrekturschleifen: string;
  /** Warenwert (Einmal-Modelle) und Beschreibung der Produkte */
  warenwert: string;
  produkte: string;
  /** Warenwert je Monat in der Monatstabelle zeigen */
  warenwertJeMonat: boolean;
  /** Vermittlerprovision (Partneragentur, Scout) */
  vermittlerModus: "none" | "percent" | "fixed";
  vermittlerWert: string;
  vermittlerBasis: "agency_share" | "budget";
  vermittlerAn: string;
  /** Gestaffelte Provision (0060): Zeilen „bis Betrag · Satz“, die letzte ohne Grenze; leer = fester Satz */
  staffel: StaffelZeile[];
  /** Agenturvergütung der Marke (AE-Provision, Agenturrabatt) in Prozent und wer sie trägt (0060) */
  abzugProzent: string;
  abzugTraeger: "shared" | "agency";
  /** Weitere Creator beim Anlegen: je Creator eine Kooperation mit denselben Angaben (0060) */
  weitereCreator: string[];
  /** Herkunft (0074): leer = keine Angabe */
  herkunft: "" | "creator" | "agency" | "brand";
}

export interface StaffelZeile {
  /** Obergrenze netto je Monat; leer in der letzten Zeile */
  bis: string;
  satz: string;
}

const EMPTY_BILLING: BillingFelder = { legal_name: "", street: "", postal_code: "", city: "", country: "", vat_id: "", register: "", email: "", reference: "", payment_terms_days: "" };

/** Monatsbasierte Modelle führen eine Tabelle je Monat; „Fest + Provision“ immer (KA-02). */
export const usesMonthTable = (m: DealModel) => MONTH_BASED.has(m);
/** Betrag je Monat statt Gesamtbetrag */
export const perMonthAmount = (m: DealModel) => m === "monthly" || m === "fixed_plus_commission" || m === "custom";
export const hasForecast = (m: DealModel) => m === "commission_only" || m === "fixed_plus_commission";

function centsToInput(c: number | null | undefined): string {
  if (c == null) return "";
  return (c / 100).toFixed(2);
}
/** Betrag für ein Textfeld im Zahlenformat der Agentur („50,00“ bzw. „50.00“); Zahlenfelder bleiben beim Punkt. */
export function betragAlsText(c: number | null | undefined): string {
  return c == null ? "" : (c / 100).toFixed(2).replace(".", decimalSeparator());
}
function bpToInput(v: number | null | undefined): string {
  if (v == null) return "";
  return String(v / 100);
}
let seq = 0;
export function positionKey(): string {
  seq += 1;
  return `p${seq}`;
}

/**
 * Gebührenart aus den Standards der Agentur. Eine Art ohne Wert (Prozent ohne Satz, Betrag ohne Betrag) gilt als „Keine“:
 * sonst stünde im Formular eine vorgewählte Gebühr mit leerem Pflichtfeld, und das Speichern scheiterte (Praxistest K-018).
 */
function standardGebuehr(s: SettingsLite | null | undefined): KoopFormular["feeMode"] {
  if (s?.agency_fee_mode === "percent" && s.agency_fee_bp != null) return "percent";
  if (s?.agency_fee_mode === "fixed" && s.agency_fee_cents != null) return "fixed";
  return "none";
}

/** Leeres Formular mit den Standards der Agentur (Gebühr wird beim Anlegen eingefroren). */
export function leeresFormular(opts: { today: string; settings: SettingsLite | null; currency: string }): KoopFormular {
  const month = opts.today.slice(0, 7);
  const s = opts.settings;
  const feeMode = standardGebuehr(s);
  return {
    title: "", talentId: "", brandId: "", newBrand: "", model: "fixed", status: "planning", currency: opts.currency,
    beginn: opts.today, ende: lastDay(month), honorar: "", satz: "",
    feeMode, feeValue: feeMode === "percent" ? bpToInput(s?.agency_fee_bp) : feeMode === "fixed" ? centsToInput(s?.agency_fee_cents) : "",
    monate: [], positionen: [], platforms: [], deliverables: "", usageRights: "", usageRightsUntil: "", link: "", contentDeadline: "",
    flatTaxed: false, visible: true, billing: { ...EMPTY_BILLING }, route: "", talentNote: "", internalNote: "",
    exklusiv: false, exklusivBis: "", exklusivFuer: "", campaignId: "", newCampaign: "", korrekturschleifen: "", warenwert: "", produkte: "",
    warenwertJeMonat: false, vermittlerModus: "none", vermittlerWert: "", vermittlerBasis: "agency_share", vermittlerAn: "",
    staffel: [], abzugProzent: "", abzugTraeger: "shared", weitereCreator: [], herkunft: "",
  };
}

/** Staffel als Formularzeilen (Beträge im Zahlenformat der Agentur) */
export function staffelZeilen(tiers: readonly CommissionTier[] | null): StaffelZeile[] {
  return (tiers ?? []).map((t) => ({ bis: t.uptoCents == null ? "" : betragAlsText(t.uptoCents), satz: String(t.percent).replace(".", decimalSeparator()) }));
}

/** Staffel aus den Formularzeilen; null = keine Staffel, Fehlertext bei ungültiger Eingabe. */
export function staffelAusZeilen(zeilen: readonly StaffelZeile[]): { tiers: CommissionTier[] | null; fehler: string | null } {
  const rows = zeilen.filter((z) => z.bis.trim() || z.satz.trim());
  if (!rows.length) return { tiers: null, fehler: null };
  const tiers: CommissionTier[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]!;
    const satz = Number(r.satz.trim().replace(",", "."));
    const last = i === rows.length - 1;
    const bis = last ? null : parseCents(r.bis);
    if (!r.satz.trim() || !Number.isFinite(satz) || satz < 0 || satz > 100) return { tiers: null, fehler: t("Jeder Satz der Staffel muss zwischen 0 und 100 Prozent liegen.", "Every rate of the tiers must be between 0 and 100 percent.") };
    if (!last && (bis == null || Number.isNaN(bis) || bis <= 0)) return { tiers: null, fehler: t("Jede Stufe außer der letzten braucht eine Obergrenze als Betrag.", "Every tier except the last needs an upper limit as an amount.") };
    tiers.push({ uptoCents: bis, percent: satz });
  }
  if (tiers.length < 2) return { tiers: null, fehler: t("Eine Staffel hat mindestens zwei Stufen, z. B. 20 % bis 5.000,00 und darüber 15 %.", "Tiers need at least two levels, e.g. 20 % up to 5,000.00 and 15 % above.") };
  if (!validTiers(tiers)) return { tiers: null, fehler: t("Die Grenzen der Staffel müssen aufsteigen; die letzte Stufe gilt ohne Obergrenze.", "The limits of the tiers must ascend; the last tier applies without an upper limit.") };
  return { tiers, fehler: null };
}

/**
 * Honorar je Monat geändert (Befund 10): Monate ohne Betrag und Monate mit dem bisherigen Standardbetrag ziehen mit; eigens
 * eingetragene Beträge bleiben.
 */
export function honorarAendern(f: KoopFormular, honorar: string): KoopFormular {
  if (!usesMonthTable(f.model) || !perMonthAmount(f.model)) return { ...f, honorar };
  const alt = f.honorar.trim();
  return { ...f, honorar, monate: f.monate.map((m) => (!m.betrag.trim() || m.betrag.trim() === alt ? { ...m, betrag: honorar } : m)) };
}

/** Zahl der Monate zwischen zwei Monatsschlüsseln (YYYY-MM) */
function monatsAbstand(von: string, bis: string): number {
  return (Number(bis.slice(0, 4)) - Number(von.slice(0, 4))) * 12 + Number(bis.slice(5, 7)) - Number(von.slice(5, 7));
}
/** Tag um n Monate verschieben (letzter Tag des Monats, wenn es den Tag nicht gibt) */
export function tagPlusMonate(tag: string, n: number): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(tag) || !n) return tag;
  const monat = addMonthKey(tag.slice(0, 7), n);
  const letzter = Number(lastDay(monat).slice(8, 10));
  return `${monat}-${String(Math.min(Number(tag.slice(8, 10)), letzter)).padStart(2, "0")}`;
}

/**
 * Beginn geändert (Befund 11): Bei Einmal-Modellen wandert das Ende mit (ein Leistungsmonat), die Content-Deadline rückt um
 * dieselbe Zahl Monate. Bei Monatsmodellen bleibt das Ende, außer es läge vor dem Beginn.
 */
export function beginnAendern(f: KoopFormular, beginn: string): KoopFormular {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(beginn) || !/^\d{4}-\d{2}-\d{2}$/.test(f.beginn)) return { ...f, beginn };
  const n = monatsAbstand(f.beginn.slice(0, 7), beginn.slice(0, 7));
  if (!usesMonthTable(f.model)) {
    return { ...f, beginn, ende: n ? lastDay(beginn.slice(0, 7)) : f.ende && f.ende.slice(0, 7) >= beginn.slice(0, 7) ? f.ende : lastDay(beginn.slice(0, 7)),
      contentDeadline: n && f.contentDeadline ? tagPlusMonate(f.contentDeadline, n) : f.contentDeadline };
  }
  return { ...f, beginn, ende: f.ende && f.ende.slice(0, 7) < beginn.slice(0, 7) ? lastDay(beginn.slice(0, 7)) : f.ende };
}

/** Formular aus einer bestehenden Kooperation (immer frisch aus der Datenbank geladen, AD-051). */
export function formularAusKooperation(deal: DealFull, allPeriods: readonly PeriodFull[], allItems: readonly ItemRow[], terms: TermsRow | undefined, settings: SettingsLite | null): KoopFormular {
  // Nachkäufe (0060) verwaltet ihr eigener Vorgang; das Formular zeigt und schickt sie nicht (deal_save lässt sie stehen)
  const nachkauf = new Set(allPeriods.filter((p) => p.kind === "follow_up").map((p) => p.id));
  const periods = allPeriods.filter((p) => !nachkauf.has(p.id));
  const items = allItems.filter((i) => !i.period_id || !nachkauf.has(i.period_id));
  const own = [...periods].filter((p) => p.deal_id === deal.id).sort((a, b) => a.month.localeCompare(b.month));
  const firstMonth = own[0]?.month.slice(0, 7) ?? deal.start_month?.slice(0, 7) ?? deal.created_at.slice(0, 7);
  const termMonths = Math.max(1, deal.months ?? own.length ?? 1);
  const lastFromTerm = addMonthKey(deal.start_month?.slice(0, 7) ?? firstMonth, termMonths - 1);
  const lastOwn = own[own.length - 1]?.month.slice(0, 7) ?? firstMonth;
  const lastMonth = lastOwn > lastFromTerm ? lastOwn : lastFromTerm;
  const monthOfPeriod = new Map(own.map((p) => [p.id, p.month.slice(0, 7)]));
  const feeMode = terms ? terms.agency_fee_mode : standardGebuehr(settings);
  const feeBp = terms ? terms.agency_fee_bp : settings?.agency_fee_bp;
  const feeCents = terms ? terms.agency_fee_cents : settings?.agency_fee_cents;
  const single = !usesMonthTable(deal.model);
  const perMonth = perMonthAmount(deal.model);
  const firstAmount = own[0]?.amount_cents ?? null;
  return {
    title: deal.title,
    talentId: deal.talent_id,
    brandId: deal.brand_id,
    newBrand: "",
    model: deal.model,
    status: deal.status,
    currency: deal.currency,
    beginn: `${firstMonth}-01`,
    ende: lastDay(lastMonth),
    honorar: single
      ? centsToInput(firstAmount ?? deal.budget_cents)
      : perMonth ? centsToInput(firstAmount) : deal.model === "installments" ? centsToInput(deal.budget_cents) : "",
    satz: bpToInput(deal.commission_bp),
    feeMode,
    feeValue: feeMode === "percent" ? bpToInput(feeBp) : feeMode === "fixed" ? centsToInput(feeCents) : "",
    monate: usesMonthTable(deal.model)
      ? own.map((p) => ({ month: p.month.slice(0, 7), betrag: centsToInput(p.amount_cents), forecast: centsToInput(p.commission_forecast_cents), deadline: p.content_deadline ?? "",
          warenwert: centsToInput(p.goods_value_cents) }))
      : [],
    positionen: items.filter((i) => i.deal_id === deal.id).map((i) => ({
      key: positionKey(), id: i.id, kind: i.kind, description: i.description, betrag: centsToInput(i.amount_cents),
      month: i.period_id ? monthOfPeriod.get(i.period_id) ?? "" : "", commissionable: i.commissionable,
    })),
    platforms: [...(deal.platforms ?? [])],
    deliverables: deal.deliverables ?? "",
    usageRights: deal.usage_rights ?? "",
    usageRightsUntil: deal.usage_rights_until ?? "",
    link: deal.link ?? "",
    contentDeadline: deal.content_deadline ?? "",
    flatTaxed: deal.products_flat_taxed,
    visible: deal.visible_to_talent,
    billing: {
      legal_name: deal.billing_legal_name ?? "", street: deal.billing_street ?? "", postal_code: deal.billing_postal_code ?? "",
      city: deal.billing_city ?? "", country: deal.billing_country ?? "", vat_id: deal.billing_vat_id ?? "", register: deal.billing_register ?? "",
      email: deal.billing_email ?? "", reference: deal.billing_reference ?? "",
      payment_terms_days: deal.billing_payment_terms_days == null ? "" : String(deal.billing_payment_terms_days),
    },
    route: deal.invoice_route ?? "",
    talentNote: deal.talent_note ?? "",
    internalNote: terms?.internal_note ?? "",
    exklusiv: !!deal.exclusive,
    exklusivBis: deal.exclusive_until ?? "",
    exklusivFuer: deal.exclusive_scope ?? "",
    campaignId: deal.campaign_id ?? "",
    newCampaign: "",
    korrekturschleifen: deal.free_revisions == null ? "" : String(deal.free_revisions),
    warenwert: single ? centsToInput(own[0]?.goods_value_cents) : "",
    produkte: deal.goods_description ?? "",
    warenwertJeMonat: !single && own.some((p) => (p.goods_value_cents ?? 0) > 0),
    vermittlerModus: terms?.referral_mode ?? "none",
    vermittlerWert: terms?.referral_mode === "percent" ? bpToInput(terms.referral_bp) : terms?.referral_mode === "fixed" ? centsToInput(terms.referral_cents) : "",
    vermittlerBasis: terms?.referral_base === "budget" ? "budget" : "agency_share",
    vermittlerAn: terms?.referral_recipient ?? "",
    // Eine vom Creator übernommene Staffel steht an der Kooperation fest und wird wie ein eigener Satz gezeigt
    staffel: staffelZeilen(tiersFromJson(deal.commission_tiers)),
    abzugProzent: deal.brand_discount_bp ? bpToInput(deal.brand_discount_bp).replace(".", decimalSeparator()) : "",
    abzugTraeger: deal.brand_discount_bearer === "agency" ? "agency" : "shared",
    weitereCreator: [],
    herkunft: deal.origin ?? "",
  };
}

/**
 * Kopie einer Kooperation (Praxisfall Kampagne): gleiche Marke, Leistungen, Beträge und Positionen, ohne Stände, Rechnungen und
 * Anforderungen. Liegt der Zeitraum in der Vergangenheit, rückt er auf den laufenden Monat (Content-Fristen rücken mit).
 */
export function formularAlsKopie(deal: DealFull, periods: readonly PeriodFull[], items: readonly ItemRow[], terms: TermsRow | undefined, settings: SettingsLite | null, today: string): KoopFormular {
  const regulaer = periods.filter((p) => p.deal_id === deal.id && p.kind !== "follow_up" && p.status !== "cancelled");
  const nachkauf = new Set(periods.filter((p) => p.kind === "follow_up").map((p) => p.id));
  const f = formularAusKooperation(deal, regulaer, items.filter((i) => !i.period_id || !nachkauf.has(i.period_id)), terms, settings);
  const start = f.beginn.slice(0, 7);
  const n = start < today.slice(0, 7) ? monatsAbstand(start, today.slice(0, 7)) : 0;
  const shift = (m: string) => (n ? addMonthKey(m, n) : m);
  return {
    ...f,
    title: t(`${deal.title} (Kopie)`, `${deal.title} (copy)`),
    status: "planning",
    // Satz neu nach Standard, außer er war an der Kooperation eingetragen
    satz: deal.commission_frozen_from ? "" : f.satz,
    staffel: deal.commission_frozen_from === "talent" ? [] : f.staffel,
    beginn: n ? `${shift(start)}-01` : f.beginn,
    ende: n ? lastDay(shift(f.ende.slice(0, 7))) : f.ende,
    contentDeadline: f.contentDeadline ? tagPlusMonate(f.contentDeadline, n) : "",
    monate: f.monate.map((m) => ({ ...m, month: shift(m.month), deadline: m.deadline ? tagPlusMonate(m.deadline, n) : "" })),
    positionen: f.positionen.map((p) => ({ ...p, key: positionKey(), id: null, month: p.month ? shift(p.month) : "" })),
    exklusivBis: f.exklusivBis ? tagPlusMonate(f.exklusivBis, n) : "",
    usageRightsUntil: f.usageRightsUntil ? tagPlusMonate(f.usageRightsUntil, n) : "",
  };
}

/** Monate von Beginn bis Ende (Kalendermonate; höchstens 36). */
export function monateImZeitraum(beginn: string, ende: string): string[] {
  if (!/^\d{4}-\d{2}/.test(beginn)) return [];
  const start = beginn.slice(0, 7);
  const end = /^\d{4}-\d{2}/.test(ende) ? ende.slice(0, 7) : start;
  if (end < start) return [start];
  const out: string[] = [];
  for (let m = start; m <= end && out.length < MAX_MONTHS + 1; m = addMonthKey(m, 1)) out.push(m);
  return out;
}

/** Monatstabelle an den Zeitraum anpassen: vorhandene Werte bleiben, neue Monate bekommen den Monatsbetrag. */
export function abgleichMonate(f: KoopFormular): KoopFormular {
  if (!usesMonthTable(f.model)) return f.monate.length ? { ...f, monate: [] } : f;
  const keys = monateImZeitraum(f.beginn, f.ende).slice(0, MAX_MONTHS);
  const have = new Map(f.monate.map((m) => [m.month, m]));
  const defaultAmount = perMonthAmount(f.model) ? f.honorar : "";
  const monate = keys.map((month) => have.get(month) ?? { month, betrag: defaultAmount, forecast: "", deadline: "", warenwert: "" });
  const same = monate.length === f.monate.length && monate.every((m, i) => m === f.monate[i]);
  return same ? f : { ...f, monate };
}

/** Honorar netto je Monat in alle Monate übernehmen. */
export function monatsbetragUebernehmen(f: KoopFormular): KoopFormular {
  return { ...f, monate: f.monate.map((m) => ({ ...m, betrag: f.honorar })) };
}

/** Einmalbudget gleichmäßig auf die Monate verteilen (AD-057); Rest-Cents gehen an die ersten Monate. */
export function gleichmaessigVerteilen(f: KoopFormular): KoopFormular {
  const total = parseCents(f.honorar);
  if (total == null || !f.monate.length) return f;
  const parts = allocateEvenly(total, f.monate.length);
  return { ...f, monate: f.monate.map((m, i) => ({ ...m, betrag: centsToInput(parts[i] ?? 0) })) };
}

export function parseCents(v: string): number | null {
  const s = v.trim();
  if (!s) return null;
  try {
    return parseAmountToCents(s);
  } catch {
    return Number.NaN;
  }
}
function parseBp(v: string): number | null {
  const s = v.trim().replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) : Number.NaN;
}

export interface WunschMonat {
  month: string;
  amount_cents: number | null;
  commission_forecast_cents: number | null;
  content_deadline: string | null;
  goods_value_cents?: number | null;
}
export interface WunschPosition {
  id: string | null;
  kind: ItemKind;
  description: string;
  amount_cents: number;
  commissionable: boolean;
  /** YYYY-MM oder null (ganze Kooperation) */
  month: string | null;
}
export interface Speicherdaten {
  deal: Record<string, unknown>;
  terms: {
    agency_fee_mode: "none" | "percent" | "fixed"; agency_fee_bp: number | null; agency_fee_cents: number | null; internal_note: string | null;
    referral_mode: "none" | "percent" | "fixed"; referral_bp: number | null; referral_cents: number | null; referral_base: "agency_share" | "budget" | null;
    referral_recipient: string | null;
  };
  monate: WunschMonat[];
  positionen: WunschPosition[];
  newBrand: string | null;
  newCampaign: { title: string } | null;
  /** Weitere Creator (nur beim Anlegen): je Creator eine Kooperation */
  weitereCreator?: string[];
}

const trimOrNull = (s: string) => (s.trim() ? s.trim() : null);

/** Prüfen und in Datenbankzeilen umwandeln. Fehler blockieren das Speichern, Rückfragen kommen danach (rueckfragen). */
export function pruefeFormular(f: KoopFormular, opts: { betraegeAus?: boolean } = {}): { fehler: string[]; daten: Speicherdaten | null } {
  // 0524: ohne „Beträge sehen“ prüft das Formular keine Beträge (es kennt sie nicht; die Datenbank lässt sie stehen)
  const geld = !opts.betraegeAus;
  const fehler: string[] = [];
  if (!f.title.trim()) fehler.push(t("Bitte einen Titel eingeben.", "Please enter a title."));
  if (!f.talentId) fehler.push(t("Bitte einen Creator wählen.", "Please choose a creator."));
  if (!f.brandId) fehler.push(t("Bitte eine Marke wählen.", "Please choose a brand."));
  if (f.brandId === NEW_BRAND && !f.newBrand.trim()) fehler.push(t("Bitte den Namen der neuen Marke eingeben.", "Please enter the name of the new brand."));
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.beginn)) fehler.push(t("Bitte den Beginn angeben.", "Please enter the start."));
  if (f.ende && f.beginn && f.ende.slice(0, 7) < f.beginn.slice(0, 7)) fehler.push(t("Das Ende liegt vor dem Beginn.", "The end is before the start."));
  const span = monateImZeitraum(f.beginn, f.ende || f.beginn);
  if (span.length > MAX_MONTHS) fehler.push(t(`Eine Kooperation läuft höchstens ${MAX_MONTHS} Monate.`, `A deal runs for at most ${MAX_MONTHS} months.`));
  const months = Math.max(1, Math.min(span.length, MAX_MONTHS));

  const honorar = parseCents(f.honorar);
  if (honorar != null && (Number.isNaN(honorar) || honorar < 0)) fehler.push(t("Das Honorar ist kein gültiger Betrag.", "The fee is not a valid amount."));
  const satz = parseBp(f.satz);
  if (satz != null && (Number.isNaN(satz) || satz < 0 || satz > 10000)) fehler.push(t("Die Provision muss zwischen 0 und 100 Prozent liegen.", "The commission must be between 0 and 100 percent."));
  let feeBp: number | null = null;
  let feeCents: number | null = null;
  if (!geld) {
    // Konditionen fasst die Speicherung ohne das Recht nicht an
  } else if (f.feeMode === "percent") {
    feeBp = parseBp(f.feeValue);
    if (feeBp == null || Number.isNaN(feeBp) || feeBp < 0 || feeBp > 10000) fehler.push(t("Die Agenturgebühr in Prozent muss zwischen 0 und 100 liegen.", "The agency fee in percent must be between 0 and 100."));
  } else if (f.feeMode === "fixed") {
    feeCents = parseCents(f.feeValue);
    if (feeCents == null || Number.isNaN(feeCents) || feeCents < 0) fehler.push(t("Bitte die Agenturgebühr netto als Betrag angeben.", "Please enter the agency fee net as an amount."));
  }
  if (f.link.trim() && !/^https?:\/\//i.test(f.link.trim())) fehler.push(t("Der Link muss mit http:// oder https:// beginnen.", "The link must start with http:// or https://."));
  if (f.billing.country.trim() && !/^[A-Za-z]{2}$/.test(f.billing.country.trim())) fehler.push(t("Das Land der Rechnungsadresse bitte als Kürzel mit zwei Buchstaben angeben (z. B. DE).", "Please enter the country of the billing address as a two-letter code (e.g. DE)."));
  const terms = f.billing.payment_terms_days.trim();
  const termDays = terms ? Number(terms) : null;
  if (termDays != null && (!Number.isInteger(termDays) || termDays < 0 || termDays > 180)) fehler.push(t("Das Zahlungsziel muss zwischen 0 und 180 Tagen liegen.", "The payment term must be between 0 and 180 days."));
  if (f.billing.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.billing.email.trim())) fehler.push(t("Die E-Mail für Rechnungen ist ungültig.", "The email for invoices is invalid."));
  // Exklusivität, Korrekturschleifen, Warenwert, Vermittlerprovision, Kampagne
  if (f.exklusiv && f.exklusivBis && !/^\d{4}-\d{2}-\d{2}$/.test(f.exklusivBis)) fehler.push(t("Bitte das Ende der Exklusivität als Datum angeben.", "Please enter the end of exclusivity as a date."));
  if (f.exklusiv && f.exklusivBis && f.beginn && f.exklusivBis < f.beginn) fehler.push(t("Die Exklusivität endet vor dem Beginn der Kooperation.", "Exclusivity ends before the deal starts."));
  if (f.exklusivFuer.trim().length > 500) fehler.push(t("Branche oder Wettbewerber der Exklusivität bitte kürzer fassen (höchstens 500 Zeichen).", "Please keep the industry or competitors of the exclusivity shorter (at most 500 characters)."));
  const schleifen = f.korrekturschleifen.trim() ? Number(f.korrekturschleifen.trim()) : null;
  if (schleifen != null && (!Number.isInteger(schleifen) || schleifen < 0 || schleifen > 20)) fehler.push(t("Die enthaltenen Korrekturschleifen müssen zwischen 0 und 20 liegen.", "The included revision rounds must be between 0 and 20."));
  const warenwert = parseCents(f.warenwert);
  if (warenwert != null && (Number.isNaN(warenwert) || warenwert < 0)) fehler.push(t("Der Warenwert ist kein gültiger Betrag.", "The value of goods is not a valid amount."));
  let refBp: number | null = null;
  let refCents: number | null = null;
  if (!geld) {
    // wie die Agenturgebühr
  } else if (f.vermittlerModus === "percent") {
    refBp = parseBp(f.vermittlerWert);
    if (refBp == null || Number.isNaN(refBp) || refBp < 0 || refBp > 10000) fehler.push(t("Die Vermittlerprovision in Prozent muss zwischen 0 und 100 liegen.", "The referral commission in percent must be between 0 and 100."));
  } else if (f.vermittlerModus === "fixed") {
    refCents = parseCents(f.vermittlerWert);
    if (refCents == null || Number.isNaN(refCents) || refCents < 0) fehler.push(t("Bitte die Vermittlerprovision als Betrag angeben.", "Please enter the referral commission as an amount."));
  }
  if (geld && f.vermittlerModus !== "none" && !f.vermittlerAn.trim()) fehler.push(t("Bitte angeben, wer die Vermittlerprovision bekommt.", "Please state who receives the referral commission."));
  if (f.campaignId === NEW_CAMPAIGN && !f.newCampaign.trim()) fehler.push(t("Bitte den Titel der neuen Kampagne eingeben.", "Please enter the title of the new campaign."));
  const staffel = staffelAusZeilen(f.staffel);
  if (staffel.fehler) fehler.push(staffel.fehler);
  const abzugBp = parseBp(f.abzugProzent);
  if (abzugBp != null && (Number.isNaN(abzugBp) || abzugBp < 0 || abzugBp > 10000)) fehler.push(t("Die Agenturvergütung der Marke muss zwischen 0 und 100 Prozent liegen.", "The brand's agency commission must be between 0 and 100 percent."));
  const weitere = [...new Set(f.weitereCreator.filter((t) => t && t !== f.talentId))];

  // Monate
  let monate: WunschMonat[] = [];
  const noMoney = NO_MONEY.has(f.model);
  if (usesMonthTable(f.model)) {
    const table: MonatZeile[] = f.monate.length ? f.monate : span.slice(0, MAX_MONTHS).map((month) => ({ month, betrag: perMonthAmount(f.model) ? f.honorar : "", forecast: "", deadline: "", warenwert: "" }));
    for (const m of table) {
      const a = f.model === "commission_only" ? null : parseCents(m.betrag);
      const fc = hasForecast(f.model) ? parseCents(m.forecast) : null;
      const gw = f.warenwertJeMonat ? parseCents(m.warenwert ?? "") : null;
      if (a != null && (Number.isNaN(a) || a < 0)) fehler.push(t(`${monthName(m.month)}: Der Betrag ist ungültig.`, `${monthName(m.month)}: the amount is invalid.`));
      if (fc != null && (Number.isNaN(fc) || fc < 0)) fehler.push(t(`${monthName(m.month)}: Die erwartete Provision ist ungültig.`, `${monthName(m.month)}: the expected commission is invalid.`));
      if (gw != null && (Number.isNaN(gw) || gw < 0)) fehler.push(t(`${monthName(m.month)}: Der Warenwert ist ungültig.`, `${monthName(m.month)}: the value of goods is invalid.`));
      // Raten: Monate ohne Betrag gehören nicht zum Ratenplan
      if (geld && f.model === "installments" && (a == null || a === 0)) continue;
      monate.push({ month: m.month, amount_cents: a, commission_forecast_cents: fc, content_deadline: m.deadline || null, goods_value_cents: gw != null && !Number.isNaN(gw) ? gw : null });
    }
    if (geld && f.model === "installments") {
      const sum = monate.reduce((s, m) => s + (m.amount_cents ?? 0), 0);
      if (honorar == null || Number.isNaN(honorar)) fehler.push(t("Bitte das Gesamthonorar für die Raten angeben.", "Please enter the total fee for the instalments."));
      else if (sum !== honorar) fehler.push(t(`Die Raten ergeben ${fmt(sum, f.currency)}, das Honorar beträgt ${fmt(honorar, f.currency)}. Bitte angleichen oder gleichmäßig verteilen.`, `The instalments add up to ${fmt(sum, f.currency)}, the fee is ${fmt(honorar, f.currency)}. Please align them or distribute evenly.`));
      if (!monate.length) fehler.push(t("Bitte mindestens eine Rate eintragen.", "Please enter at least one instalment."));
    }
  } else {
    monate = [{
      month: f.beginn.slice(0, 7), amount_cents: noMoney ? null : honorar != null && !Number.isNaN(honorar) ? honorar : null, commission_forecast_cents: null,
      content_deadline: f.contentDeadline || null, goods_value_cents: warenwert != null && !Number.isNaN(warenwert) ? warenwert : null,
    }];
  }
  const monthSet = new Set(monate.map((m) => m.month));

  // Positionen
  const positionen: WunschPosition[] = [];
  for (const p of f.positionen) {
    const a = parseCents(p.betrag);
    if (!p.description.trim()) fehler.push(t(`Position „${p.betrag || t("ohne Betrag", "without amount")}“: Bitte eine Beschreibung eingeben.`, `Item “${p.betrag || t("ohne Betrag", "without amount")}”: please enter a description.`));
    if (geld && (a == null || Number.isNaN(a) || a < 0)) fehler.push(t(`Position „${p.description || t("ohne Beschreibung", "without description")}“: Bitte einen gültigen Betrag eingeben.`, `Item “${p.description || t("ohne Beschreibung", "without description")}”: please enter a valid amount.`));
    positionen.push({
      id: p.id, kind: p.kind, description: p.description.trim(), amount_cents: a != null && !Number.isNaN(a) ? a : 0, commissionable: p.commissionable,
      month: p.month && monthSet.has(p.month) ? p.month : null,
    });
  }

  if (fehler.length) return { fehler, daten: null };

  const budget = f.model === "commission_only" || noMoney
    ? null
    : usesMonthTable(f.model) ? (f.model === "installments" ? honorar : monate.reduce((s, m) => s + (m.amount_cents ?? 0), 0)) : honorar;
  const firstMonth = [...monthSet].sort()[0] ?? f.beginn.slice(0, 7);
  const deal: Record<string, unknown> = {
    title: f.title.trim(),
    talent_id: f.talentId,
    model: f.model,
    status: f.status,
    currency: f.currency,
    budget_cents: budget,
    // Staffel geht vor; der Satz der ersten Stufe steht als Satz (Wege, die einen Satz erwarten)
    commission_bp: staffel.tiers ? Math.round(staffel.tiers[0]!.percent * 100) : satz,
    commission_tiers: tiersToJson(staffel.tiers),
    brand_discount_bp: abzugBp && !Number.isNaN(abzugBp) ? abzugBp : null,
    brand_discount_bearer: f.abzugTraeger,
    origin: f.herkunft || null,
    start_month: `${f.beginn.slice(0, 7) < firstMonth ? f.beginn.slice(0, 7) : firstMonth}-01`,
    months,
    deliverables: trimOrNull(f.deliverables),
    talent_note: trimOrNull(f.talentNote),
    visible_to_talent: f.visible,
    platforms: f.platforms,
    usage_rights: trimOrNull(f.usageRights),
    usage_rights_until: f.usageRightsUntil || null,
    link: trimOrNull(f.link),
    content_deadline: f.contentDeadline || null,
    products_flat_taxed: f.flatTaxed,
    billing_legal_name: trimOrNull(f.billing.legal_name),
    billing_street: trimOrNull(f.billing.street),
    billing_postal_code: trimOrNull(f.billing.postal_code),
    billing_city: trimOrNull(f.billing.city),
    billing_country: f.billing.country.trim() ? f.billing.country.trim().toUpperCase() : null,
    billing_vat_id: trimOrNull(f.billing.vat_id),
    billing_register: trimOrNull(f.billing.register),
    billing_email: trimOrNull(f.billing.email),
    billing_reference: trimOrNull(f.billing.reference),
    billing_payment_terms_days: termDays,
    invoice_route: f.route || null,
    exclusive: f.exklusiv,
    exclusive_until: f.exklusiv ? f.exklusivBis || null : null,
    exclusive_scope: f.exklusiv ? trimOrNull(f.exklusivFuer) : null,
    campaign_id: f.campaignId && f.campaignId !== NEW_CAMPAIGN ? f.campaignId : null,
    free_revisions: schleifen,
    goods_description: trimOrNull(f.produkte),
  };
  if (f.brandId !== NEW_BRAND) deal.brand_id = f.brandId;
  return {
    fehler: [],
    daten: {
      deal,
      terms: {
        agency_fee_mode: f.feeMode, agency_fee_bp: feeBp, agency_fee_cents: feeCents, internal_note: trimOrNull(f.internalNote),
        referral_mode: f.vermittlerModus, referral_bp: refBp, referral_cents: refCents,
        referral_base: f.vermittlerModus === "percent" ? f.vermittlerBasis : null, referral_recipient: f.vermittlerModus === "none" ? null : trimOrNull(f.vermittlerAn),
      },
      monate,
      positionen,
      newBrand: f.brandId === NEW_BRAND ? f.newBrand.trim() : null,
      newCampaign: f.campaignId === NEW_CAMPAIGN ? { title: f.newCampaign.trim() } : null,
      ...(weitere.length ? { weitereCreator: weitere } : {}),
    },
  };
}

/** Beträge in der Währung der Kooperation und im Zahlenformat der Agentur (Befund 23). */
function fmt(c: number, currency: string): string {
  return money(c, currency);
}
const deTag = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

// ── Monatsabgleich mit Schutz (KA-06, KA-07, AD-064) ────────────────────────

export interface MonatsPlan {
  neu: WunschMonat[];
  aendern: { id: string; month: string; patch: Record<string, unknown> }[];
  loeschen: { id: string; month: string }[];
  /** Nicht mehr gewünscht, aber geschützt: bleibt bestehen */
  behalten: { id: string; month: string; grund: string }[];
  /** Gebucht: Betrag bleibt unverändert */
  festgehalten: { id: string; month: string }[];
}

export function schutzGrund(m: MonatInfo, invoicesReadFailed: boolean): string | null {
  const p = m.period;
  const geschuetzt = isMonthProtected({
    invoices: m.records,
    invoicesReadFailed,
    manualBookedAt: p.booked_at,
    customerPaidAt: m.booking.customerPaidAt,
    talentPaidAt: m.booking.talentPaidAt,
    invoiceRequestedAt: p.invoice_requested_at,
  });
  // Nicht lesbare Rechnungszeilen zählen wie Rechnungen (lieber behalten)
  const unreadable = m.invoices.some((e) => !e.record && !["withdrawn", "discarded"].includes(e.row.status));
  // Im bisherigen System abgerechnet bzw. bezahlt (0064): bleibt wie in der Datenbank (app.period_protection) stehen
  if (p.imported_state && !p.booked_at && !geschuetzt && !unreadable) return t("aus dem bisherigen System übernommen", "taken over from the previous system");
  if (!geschuetzt && !unreadable) return null;
  if (invoicesReadFailed) return t("Rechnungen konnten nicht gelesen werden", "Invoices could not be read");
  if (p.booked_at) return t("erledigt", "settled");
  if (m.records.some((r) => isLive(r)) || unreadable) return t("Rechnung vorhanden", "Invoice exists");
  if (p.invoice_requested_at) return t("Rechnung angefordert", "Invoice requested");
  return t("Zahlung vermerkt", "Payment noted");
}

export function planeMonate(bestand: readonly MonatInfo[], wunsch: readonly WunschMonat[], opts: { invoicesReadFailed: boolean }): MonatsPlan {
  const plan: MonatsPlan = { neu: [], aendern: [], loeschen: [], behalten: [], festgehalten: [] };
  const byMonth = new Map(bestand.map((m) => [m.month, m]));
  const wanted = new Set(wunsch.map((w) => w.month));
  for (const w of wunsch) {
    const have = byMonth.get(w.month);
    if (!have) {
      plan.neu.push(w);
      continue;
    }
    const p = have.period;
    const patch: Record<string, unknown> = {};
    const booked = !!p.booked_at || have.booking.booked;
    if (booked && ((w.amount_cents ?? null) !== (p.amount_cents ?? null) || (w.commission_forecast_cents ?? null) !== (p.commission_forecast_cents ?? null))) {
      plan.festgehalten.push({ id: p.id, month: w.month });
    } else {
      if ((w.amount_cents ?? null) !== (p.amount_cents ?? null)) patch.amount_cents = w.amount_cents;
      if ((w.commission_forecast_cents ?? null) !== (p.commission_forecast_cents ?? null)) patch.commission_forecast_cents = w.commission_forecast_cents;
    }
    if ((w.content_deadline ?? null) !== (p.content_deadline ?? null)) patch.content_deadline = w.content_deadline;
    if (Object.keys(patch).length) plan.aendern.push({ id: p.id, month: w.month, patch });
  }
  for (const m of bestand) {
    if (wanted.has(m.month)) continue;
    const grund = schutzGrund(m, opts.invoicesReadFailed);
    if (grund) plan.behalten.push({ id: m.period.id, month: m.month, grund });
    else plan.loeschen.push({ id: m.period.id, month: m.month });
  }
  plan.neu.sort((a, b) => a.month.localeCompare(b.month));
  return plan;
}

/**
 * Praxistest K-146: Der Agenturanteil steht an der Kooperation und gilt damit für jeden Monat, auch für schon abgerechnete.
 * `creator`: Monate, deren Creator-Seite feststeht (festgeschriebene Rechnung des Creators, Gutschrift oder Provisionsrechnung):
 * dort darf sich der Satz nicht mehr ändern. `marke`: Monate, die nur an die Marke abgerechnet oder verbucht sind: der Satz darf
 * sich ändern, aber nur mit einer Rückfrage, die Monat und neuen Creator-Anteil nennt.
 */
export interface SatzSchutz { creator: string[]; marke: { month: string; amount_cents: number | null }[] }

const FEST = new Set(["approved", "sent", "paid"]);
const CREATOR_SEITE = new Set(["talent_to_agency", "agency_to_talent", "talent_to_brand"]);

export function satzSchutz(bestand: readonly MonatInfo[]): SatzSchutz {
  const out: SatzSchutz = { creator: [], marke: [] };
  for (const m of bestand) {
    if (m.period.status === "cancelled") continue;
    const fest = m.invoices.filter((e) => FEST.has(e.row.status) && !e.row.credit_for);
    if (fest.some((e) => CREATOR_SEITE.has(e.row.kind))) out.creator.push(m.month);
    else if (fest.length > 0 || !!m.period.booked_at || m.booking.booked || !!m.period.imported_state) out.marke.push({ month: m.month, amount_cents: m.period.amount_cents ?? null });
  }
  return out;
}

/**
 * Ohne „Beträge sehen“ (0512/0524, Test-Chat 04.11.2026): Das Formular kennt die Beträge nicht. Es schickt deshalb keine
 * Geldfelder der Kooperation mit (die Datenbank lässt sie stehen) und beim Ändern die Monate genau so, wie sie gespeichert sind
 * (`bestandMonate`, Monate im Format JJJJ-MM): Fristen der vorhandenen Monate bleiben änderbar, neue oder entfernte Monate
 * entstehen nicht. Beim Anlegen (`bestandMonate` null) bleiben die Monate aus dem Zeitraum, ohne Betrag.
 */
export function ohneBetraege(daten: Speicherdaten, bestandMonate: readonly string[] | null): Speicherdaten {
  const { budget_cents: _b, commission_bp: _c, commission_tiers: _s, brand_discount_bp: _a, ...deal } = daten.deal;
  const leer = (m: WunschMonat): WunschMonat => ({ ...m, amount_cents: null, commission_forecast_cents: null, goods_value_cents: null });
  const monate = bestandMonate
    ? bestandMonate.map((month) => leer(daten.monate.find((m) => m.month === month) ?? { month, amount_cents: null, commission_forecast_cents: null, content_deadline: null }))
    : daten.monate.map(leer);
  return { ...daten, deal, monate, positionen: daten.positionen.filter((p) => !!p.id) };
}

/** Rückfragen vor dem Speichern (AD-053, AD-063, KA-09): Folgen zeigen, bevor etwas verloren geht. */
export function rueckfragen(f: KoopFormular, daten: Speicherdaten, ctx: {
  bestand: DealFull | null; plan: MonatsPlan; offeneMonate: number; hatBelege: boolean; brandName: (id: string) => string; talentName: (id: string) => string;
  /** Unterschriebener Vertrag zur Kooperation mit den bisherigen Werten (Summe aus Monaten und Positionen) */
  vertrag?: { unterschrieben: boolean; alteSumme: number } | null;
  /** Exklusivitätskonflikte mit anderen Kooperationen desselben Creators (Befund 9), als Text */
  exklusiv?: readonly string[];
  /** Schon abgerechnete Monate, für die ein geänderter Agenturanteil Folgen hat (K-146) */
  satzSchutz?: SatzSchutz | null;
  /** Ohne „Beträge sehen“ (0524): keine Rückfragen zu Honorar, Beträgen und Agenturanteil (das Formular kennt sie nicht) */
  betraegeAus?: boolean;
}): { fehler: string[]; fragen: string[] } {
  const fehler: string[] = [];
  const fragen: string[] = [];
  const b = ctx.bestand;
  for (const x of ctx.exklusiv ?? []) fragen.push(x);
  if (!b && daten.weitereCreator?.length) {
    fragen.push(t(`Es entstehen ${daten.weitereCreator.length + 1} Kooperationen mit denselben Angaben: ${[f.talentId, ...daten.weitereCreator].map((t) => ctx.talentName(t)).join(", ")}.`, `${daten.weitereCreator.length + 1} deals with the same details will be created: ${[f.talentId, ...daten.weitereCreator].map((t) => ctx.talentName(t)).join(", ")}.`));
  }
  const sum = (list: readonly WunschMonat[]) => list.reduce((s, m) => s + (m.amount_cents ?? 0), 0);
  if (b) {
    if (b.talent_id !== f.talentId) {
      if (ctx.hatBelege) fehler.push(t("Der Creator kann nicht gewechselt werden, weil es schon Rechnungen, Verträge oder eine Rechnungsanforderung zu dieser Kooperation gibt.", "The creator cannot be changed because there are already invoices, contracts or an invoice request for this deal."));
      else fragen.push(t(`Der Creator wechselt von ${ctx.talentName(b.talent_id)} zu ${ctx.talentName(f.talentId)}.`, `The creator changes from ${ctx.talentName(b.talent_id)} to ${ctx.talentName(f.talentId)}.`));
    }
    if (f.brandId !== NEW_BRAND && b.brand_id !== f.brandId) {
      if (ctx.hatBelege) fehler.push(t("Die Marke kann nicht gewechselt werden, weil es schon Rechnungen oder Verträge zu dieser Kooperation gibt.", "The brand cannot be changed because there are already invoices or contracts for this deal."));
      else fragen.push(t(`Die Marke wechselt von ${ctx.brandName(b.brand_id)} zu ${ctx.brandName(f.brandId)}.`, `The brand changes from ${ctx.brandName(b.brand_id)} to ${ctx.brandName(f.brandId)}.`));
    }
    // Praxistest K-146: geänderter Agenturanteil bei schon abgerechneten Monaten
    const altBp = b.commission_bp ?? null;
    const neuBp = (daten.deal.commission_bp as number | null | undefined) ?? null;
    const staffelAlt = JSON.stringify(tiersToJson(tiersFromJson(b.commission_tiers)) ?? null);
    const staffelNeu = JSON.stringify(daten.deal.commission_tiers ?? null);
    const schutz = ctx.betraegeAus ? null : ctx.satzSchutz;
    if (schutz && (altBp !== neuBp || staffelAlt !== staffelNeu)) {
      const pz = (bp: number | null) => bp == null ? t("Standard", "default") : `${String(bp / 100).replace(".", decimalSeparator())} %`;
      if (schutz.creator.length) {
        const liste = schutz.creator.map((m) => monthName(m)).join(", ");
        fehler.push(t(
          `Die Provision gilt für alle Monate dieser Kooperation. Für ${liste} hat der Creator schon abgerechnet, deshalb bleibt sie bei ${pz(altBp)}. Soll ab einem späteren Monat ein anderer Satz gelten, lege die restlichen Monate als neue Kooperation an.`,
          `The commission applies to every month of this deal. For ${liste} the creator has already invoiced, so it stays at ${pz(altBp)}. If a different rate should apply from a later month, create the remaining months as a new deal.`));
      } else if (schutz.marke.length) {
        const einfach = altBp != null && neuBp != null && staffelAlt === "null" && staffelNeu === "null";
        const anteil = (cents: number, bp: number) => cents - Math.round((cents * bp) / 10000);
        const liste = schutz.marke.map((m) => einfach && m.amount_cents
          ? `${monthName(m.month)} (${t("Creator-Anteil", "creator's share")} ${fmt(anteil(m.amount_cents, altBp!), f.currency)} → ${fmt(anteil(m.amount_cents, neuBp!), f.currency)})`
          : monthName(m.month)).join(", ");
        fragen.push(t(
          `Die Provision ändert sich von ${pz(altBp)} auf ${pz(neuBp)}, auch für schon an die Marke abgerechnete Monate: ${liste}. Der Creator sieht danach den neuen Anteil.`,
          `The commission changes from ${pz(altBp)} to ${pz(neuBp)}, also for months already invoiced to the brand: ${liste}. The creator then sees the new share.`));
      }
    }
    if (b.title.trim() !== f.title.trim()) fragen.push(t(`Der Titel ändert sich von „${b.title}“ zu „${f.title.trim()}“.`, `The title changes from “${b.title}” to “${f.title.trim()}”.`));
    if (b.model !== f.model) fragen.push(t(`Das Vergütungsmodell wechselt von ${MODEL_LABEL[b.model]} zu ${MODEL_LABEL[f.model]}.`, `The remuneration model changes from ${MODEL_LABEL[b.model]} to ${MODEL_LABEL[f.model]}.`));
  }
  // Einmal-Kooperation: der Leistungsmonat wird verschoben (nicht verdoppelt)
  const verschoben = !!b && !usesMonthTable(f.model) && ctx.plan.neu.length === 1 && (ctx.plan.behalten.length + ctx.plan.loeschen.length) === 1;
  if (verschoben) {
    const alt = ctx.plan.behalten[0]?.month ?? ctx.plan.loeschen[0]!.month;
    const frist = f.contentDeadline && b?.content_deadline && f.contentDeadline !== b.content_deadline
      ? t(` Die Content-Deadline rückt von ${deTag(b.content_deadline)} auf ${deTag(f.contentDeadline)}.`, ` The content deadline moves from ${deTag(b.content_deadline)} to ${deTag(f.contentDeadline)}.`) : "";
    fragen.push(t(`Der Leistungsmonat wird von ${monthName(alt)} auf ${monthName(ctx.plan.neu[0]!.month)} verschoben.${frist} Eine bestehende Anforderung zieht mit; gibt es schon Rechnungen, lehnt die Datenbank das Verschieben ab.`, `The service month is moved from ${monthName(alt)} to ${monthName(ctx.plan.neu[0]!.month)}.${frist} An existing request moves along; if there are already invoices, the database refuses the move.`));
  } else if (ctx.plan.neu.length && b) {
    const total = sum(ctx.plan.neu);
    fragen.push(t(`${ctx.plan.neu.length === 1 ? t("1 Monat kommt", "1 month is added") : t(`${ctx.plan.neu.length} Monate kommen`, `${ctx.plan.neu.length} months are added`)} hinzu${total ? t(`, zusammen ${fmt(total, f.currency)}`, `, together ${fmt(total, f.currency)}`) : ""}: ${ctx.plan.neu.map((m) => monthName(m.month)).join(", ")}.`, `${ctx.plan.neu.length === 1 ? t("1 Monat kommt", "1 month is added") : t(`${ctx.plan.neu.length} Monate kommen`, `${ctx.plan.neu.length} months are added`)}${total ? t(`, zusammen ${fmt(total, f.currency)}`, `, together ${fmt(total, f.currency)}`) : ""}: ${ctx.plan.neu.map((m) => monthName(m.month)).join(", ")}.`));
  }
  if (!verschoben && ctx.plan.loeschen.length) fragen.push(t(`${ctx.plan.loeschen.length === 1 ? t("1 Monat wird", "1 month is") : t(`${ctx.plan.loeschen.length} Monate werden`, `${ctx.plan.loeschen.length} months are`)} entfernt: ${ctx.plan.loeschen.map((m) => monthName(m.month)).join(", ")}.`, `${ctx.plan.loeschen.length === 1 ? t("1 Monat wird", "1 month is") : t(`${ctx.plan.loeschen.length} Monate werden`, `${ctx.plan.loeschen.length} months are`)} removed: ${ctx.plan.loeschen.map((m) => monthName(m.month)).join(", ")}.`));
  if (!verschoben && ctx.plan.behalten.length) fragen.push(t(`${ctx.plan.behalten.length === 1 ? t("1 Monat bleibt", "1 month is") : t(`${ctx.plan.behalten.length} Monate bleiben`, `${ctx.plan.behalten.length} months are`)} erhalten, weil dort schon echte Daten liegen: ${ctx.plan.behalten.map((m) => `${monthName(m.month)} (${m.grund})`).join(", ")}.`, `${ctx.plan.behalten.length === 1 ? t("1 Monat bleibt", "1 month is") : t(`${ctx.plan.behalten.length} Monate bleiben`, `${ctx.plan.behalten.length} months are`)} kept because real data already exists there: ${ctx.plan.behalten.map((m) => `${monthName(m.month)} (${m.grund})`).join(", ")}.`));
  if (!ctx.betraegeAus && ctx.plan.festgehalten.length) fragen.push(t(`Erledigte Monate behalten ihren Betrag: ${ctx.plan.festgehalten.map((m) => monthName(m.month)).join(", ")}.`, `Settled months keep their amount: ${ctx.plan.festgehalten.map((m) => monthName(m.month)).join(", ")}.`));
  const geld = !ctx.betraegeAus;
  if (geld && f.model === "barter" && (daten.monate[0]?.amount_cents ?? 0) > 0) fragen.push(t("Barter mit Honorar: Das Honorar wird geteilt, abgerechnet und zählt als Umsatz. Der Warenwert wird nie geteilt, nie abgerechnet und zählt nie als Umsatz.", "Barter with fee: the fee is split, invoiced and counts as revenue. The value of goods is never split, never invoiced and never counts as revenue."));
  if (geld && NO_MONEY.has(f.model) && daten.positionen.some((p) => p.amount_cents > 0)) fragen.push(t(`${MODEL_LABEL[f.model]} hat keine Rechnung. Beträge der Zusatzpositionen zählen nicht als Umsatz.`, `${MODEL_LABEL[f.model]} has no invoice. Amounts of extra items do not count as revenue.`));
  if (geld && usesMonthTable(f.model) && f.model !== "commission_only" && f.model !== "installments") {
    const empty = daten.monate.filter((m) => !m.amount_cents).length;
    if (empty) fragen.push(t(`${empty === 1 ? t("1 Monat hat", "1 month has") : t(`${empty} Monate haben`, `${empty} months have`)} keinen Betrag.`, `${empty === 1 ? t("1 Monat hat", "1 month has") : t(`${empty} Monate haben`, `${empty} months have`)} no amount.`));
  }
  if (geld && !usesMonthTable(f.model) && !NO_MONEY.has(f.model) && f.model !== "barter" && !daten.monate[0]?.amount_cents) fragen.push(t("Es ist kein Honorar eingetragen.", "No fee has been entered."));
  // Unterschriebener Vertrag: geänderte Beträge, Leistungen oder Nutzungsrechte weichen danach vom Vertrag ab (Nachtrag nötig)
  if (b && ctx.vertrag?.unterschrieben) {
    const neu = !geld ? ctx.vertrag.alteSumme : daten.monate.reduce((s0, m) => s0 + (m.amount_cents ?? 0), 0) + daten.positionen.reduce((s0, p) => s0 + (p.amount_cents ?? 0), 0);
    const teile = [
      neu !== ctx.vertrag.alteSumme ? t(`Betrag ${fmt(ctx.vertrag.alteSumme, f.currency)} → ${fmt(neu, f.currency)}`, `Amount ${fmt(ctx.vertrag.alteSumme, f.currency)} → ${fmt(neu, f.currency)}`) : null,
      (b.deliverables ?? "").trim() !== f.deliverables.trim() ? t("Leistungen", "Services") : null,
      (b.usage_rights ?? "").trim() !== f.usageRights.trim() || (b.usage_rights_until ?? "") !== (f.usageRightsUntil || "") ? t("Nutzungsrechte", "Usage rights") : null,
    ].filter(Boolean);
    if (teile.length) fragen.push(t(`Zu dieser Kooperation gibt es einen unterschriebenen Vertrag. Geändert: ${teile.join(", ")}. Der Vertrag nennt danach andere Werte; bitte einen Nachtrag erstellen (Verträge · Neue Fassung).`, `There is a signed contract for this deal. Changed: ${teile.join(", ")}. The contract will then state different values; please create an addendum (Contracts · New version).`));
  }
  if (f.status === "done" && ctx.offeneMonate > 0) fragen.push(ctx.offeneMonate === 1
    ? t("Status „Abgeschlossen“, obwohl 1 Monat noch offen ist. Die Kooperation bleibt aktiv, bis alles erledigt ist.", "Status “Completed” although 1 month is still open. The deal stays active until everything is done.")
    : t(`Status „Abgeschlossen“, obwohl ${ctx.offeneMonate} Monate noch offen sind. Die Kooperation bleibt aktiv, bis alles erledigt ist.`, `Status “Completed” although ${ctx.offeneMonate} months are still open. The deal stays active until everything is done.`));
  return { fehler, fragen };
}

/** Standard „provisionspflichtig“ je Art einer neuen Zusatzposition (KZ-04), aus dem Katalog der Agentur. */
export function neuePosition(kind: ItemKind = "usage_rights", katalog: readonly CatalogEntry[] = []): PositionZeile {
  return { key: positionKey(), id: null, kind, description: "", betrag: "", month: "", commissionable: itemKindCommissionable(kind, katalog) };
}
